import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { post as apiPost, get as apiGet, put as apiPut } from '../services/api/apiClient';
import Sidebar from '../components/Sidebar';
import { Button, IconButton, DropdownMenu, PillButton } from '../components/ui/Button';
import {
  Card,
  CardPreview,
  CardImage,
  CardContent,
  CardTitle,
  CardDescription,
  CardMenu,
  CardBadge,
  MindPalaceProjectCard,
  ContentSpinner,
  useToast,
} from '../components/ui';

// Workflow automation templates - all in one list
const workflowTemplates = [
  {
    id: 'content-generator',
    title: 'Content Generator',
    description: 'Generate content from trending topics and keywords',
    icon: 'mdi:robot-outline',
    type: 'Content'
  },
  {
    id: 'random-post',
    title: 'Random Post',
    description: 'One-click random text post from story library',
    icon: 'mdi:dice-multiple',
    type: 'Content'
  },
  {
    id: 'idea-to-post',
    title: 'Idea to Post',
    description: 'Transform your ideas into engaging posts',
    icon: 'mdi:lightbulb-on',
    type: 'Content'
  },
  {
    id: 'creator-to-script',
    title: 'Creator to Script',
    description: 'Generate Reels scripts inspired by creators',
    icon: 'mdi:movie-open-outline',
    type: 'Content'
  },
  {
    id: 'dashboard-insights',
    title: 'Account & Insights',
    description: 'View your Threads performance metrics',
    icon: 'mdi:chart-line',
    type: 'Analytics'
  },
  {
    id: 'fake-text-generator',
    title: 'Fake Text Generator',
    description: 'Create realistic fake text message screenshots',
    icon: 'mdi:message-text',
    type: 'Automation'
  },
  {
    id: 'fake-notification-generator',
    title: 'Fake Notification Generator',
    description: 'Generate fake notification screenshots for content',
    icon: 'mdi:bell',
    type: 'Automation'
  },
  {
    id: 'fake-call-generator',
    title: 'Fake Call Generator',
    description: 'Create fake incoming/outgoing call screenshots',
    icon: 'mdi:phone',
    type: 'Automation'
  }
];

function ProjectsPage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { toast, confirm } = useToast();

  // Check if we're on /home to show Quick Start section
  const isHomePage = location.pathname === '/home';
  const [projects, setProjects] = useState([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  // Template category filter
  const [templateCategory, setTemplateCategory] = useState('All');
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const hasLoadedRef = useRef(false);
  const retryCountRef = useRef(0);

  // 自動經營 Projects (結合舊的 settings 和新的 projects)
  const [autoManagedProjects, setAutoManagedProjects] = useState([]);
  const [isLoadingAutoManaged, setIsLoadingAutoManaged] = useState(true);

  // 草稿進度 (content-generator 類型的 projects)
  const [draftProgress, setDraftProgress] = useState([]);
  const [isLoadingDrafts, setIsLoadingDrafts] = useState(true);

  // Load projects from database - 順序執行避免 rate limit
  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    const loadAllData = async () => {
      // 使用單一 API 請求獲取所有 projects，然後在前端過濾
      await loadProjects();
      // 等待 500ms 避免 rate limit
      await new Promise(resolve => setTimeout(resolve, 500));
      await loadAutoManagedProjects();
    };

    loadAllData();
  }, []);

  // 草稿現在在 loadProjects 中一起載入，不需要獨立的函數

  // 繼續編輯草稿
  const handleContinueDraft = (project) => {
    if (!project) return;

    // 根據草稿的 currentStep 導航到該步驟，使用 URL 參數傳遞 projectId
    // currentStep 1 = Step 1 已完成，準備進入 Step 2
    // currentStep 2 = Step 2 已完成，準備進入 Step 3
    // currentStep 3 = Step 3 已完成，準備進入 Step 4
    // currentStep 4 = Step 4 已完成或進行中
    const step = project.currentStep || 1;

    // 導航到用戶最後所在的步驟，使用 URL 參數傳遞 projectId
    if (step === 1) {
      // Step 1 完成 → 進入 Step 2
      navigate(`/templates/content-generator/step2?projectId=${project.id}`);
    } else if (step === 2) {
      // Step 2 完成或進行中 → 進入 Step 2
      navigate(`/templates/content-generator/step2?projectId=${project.id}`);
    } else if (step === 3) {
      // Step 3 完成或進行中 → 進入 Step 3
      navigate(`/templates/content-generator/step3?projectId=${project.id}`);
    } else if (step >= 4) {
      // Step 4 進行中 → 進入 Step 4
      navigate(`/templates/content-generator/step4?projectId=${project.id}`);
    } else {
      // 預設進入 Step 2
      navigate(`/templates/content-generator/step2?projectId=${project.id}`);
    }
  };

  // 刪除草稿
  const handleDeleteDraft = async (projectId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/projects/${projectId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setDraftProgress(prev => prev.filter(p => p.id !== projectId));
      }
    } catch (error) {
      console.error('Failed to delete draft:', error);
    }
  };

  // 載入自動經營的 Projects (同時支援新舊格式)
  const loadAutoManagedProjects = async () => {
    try {
      setIsLoadingAutoManaged(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      // 同時獲取新的 projects 和舊的 settings
      const [projectsRes, settingsRes] = await Promise.all([
        fetch('/api/projects/user-projects?type=auto-management', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch('/api/auto-management/list', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      const projectsData = await projectsRes.json();
      const settingsData = await settingsRes.json();

      const allItems = [];
      const processedAccountIds = new Set();

      // 1. 先處理新的 projects (優先)
      if (projectsData.success && projectsData.projects) {
        projectsData.projects.forEach(project => {
          try {
            const settings = JSON.parse(project.contentDescription || '{}');
            allItems.push({ ...project, settings, isProject: true });
            if (settings.accountId) {
              processedAccountIds.add(settings.accountId);
            }
          } catch {
            allItems.push({ ...project, settings: {}, isProject: true });
          }
        });
      }

      // 2. 再處理舊的 settings (只顯示 enabled 且未被 project 覆蓋的)
      if (settingsData.success && settingsData.settings) {
        settingsData.settings.forEach(setting => {
          if (setting.enabled && !processedAccountIds.has(setting.account_id)) {
            allItems.push({
              id: `settings-${setting.id}`,
              name: `${setting.account_name} 自動發文`,
              settings: {
                accountId: setting.account_id,
                accountName: setting.account_name,
                platform: setting.platform,
                strategy: setting.strategy,
                postingTimes: setting.posting_times,
                topics: setting.topics,
                keywords: setting.keywords
              },
              isProject: false,
              settingsId: setting.id
            });
          }
        });
      }

      setAutoManagedProjects(allItems);
    } catch (error) {
      console.error('Failed to load auto managed projects:', error);
    } finally {
      setIsLoadingAutoManaged(false);
    }
  };

  const loadProjects = async (force = false) => {
    // 快取機制：如果距離上次載入不到 60 秒，且不是強制刷新，則跳過
    const CACHE_DURATION = 60000; // 60 秒
    const now = Date.now();

    if (!force && lastFetchTime && (now - lastFetchTime) < CACHE_DURATION) {
      console.log('📦 Using cached projects data (loaded', Math.round((now - lastFetchTime) / 1000), 'seconds ago)');
      return;
    }

    // 限制重試次數,避免無限循環
    const MAX_RETRIES = 3;

    try {
      setIsLoadingProjects(true);
      const response = await apiGet('/projects/user-projects');

      // Demo projects removed - all features are now available
      const demoProjects = [];

      if (response.success) {
        // 從所有 projects 中過濾出 content-generator 草稿
        const contentGeneratorDrafts = response.projects.filter(p =>
          p.type === 'content-generator' &&
          p.generationStatus !== 'published' &&
          p.generationStatus !== 'saved'
        );
        setDraftProgress(contentGeneratorDrafts);
        setIsLoadingDrafts(false);

        // 過濾掉 content-generator 類型的 projects（它們會顯示在草稿區）
        const regularProjects = response.projects.filter(p => p.type !== 'content-generator');

        // 合併真實專案和假專案
        const allProjects = [...regularProjects, ...demoProjects];
        setProjects(allProjects);
        setLastFetchTime(now);
        retryCountRef.current = 0; // 成功後重置重試計數
        console.log('✅ Projects loaded and cached:', allProjects.length, 'projects');
        console.log('✅ Drafts loaded:', contentGeneratorDrafts.length, 'drafts');
      }
      setIsLoadingProjects(false);
    } catch (error) {
      // Handle rate limit errors gracefully (check both standard and custom error formats)
      if (error.response?.status === 429 || error.status === 429 || error.message?.includes('Too many requests')) {
        if (retryCountRef.current < MAX_RETRIES) {
          retryCountRef.current += 1;
          // 使用指數退避策略: 5秒, 15秒, 60秒
          const retryDelay = Math.min(5000 * Math.pow(3, retryCountRef.current - 1), 60000);
          console.warn(`⚠️ Rate limit reached (retry ${retryCountRef.current}/${MAX_RETRIES}), will retry in ${retryDelay / 1000} seconds...`);
          setTimeout(() => loadProjects(true), retryDelay);
        } else {
          console.error('❌ Max retries reached. Please refresh the page to try again.');
          retryCountRef.current = 0;
        }
        setIsLoadingProjects(false);
        return;
      }
      console.error('Failed to load projects:', error);
      setIsLoadingProjects(false);
    }
  };

  const handleTemplateClick = (templateId) => {
    console.log('Template clicked:', templateId);
    if (templateId === 'content-generator') {
      navigate('/templates/content-generator');
    } else if (templateId === 'dashboard-insights') {
      navigate('/dashboard-insights');
    } else if (templateId === 'keyword-on-threads-to-post' || templateId === 'google-trending-to-post' || templateId === 'youtube-to-post') {
      // All three templates now redirect to unified content generator
      navigate('/templates/content-generator');
    } else if (templateId === 'idea-to-post') {
      navigate('/templates/idea-to-post');
    } else if (templateId === 'random-post') {
      navigate('/templates/random-post');
    } else if (templateId === 'creator-to-script') {
      navigate('/templates/creator-to-script');
    } else if (templateId === 'comment-management') {
      navigate('/comment-management');
    } else if (templateId === 'fake-text-generator') {
      navigate('/tools/fake-text-generator');
    } else if (templateId === 'fake-notification-generator') {
      navigate('/tools/fake-notification-generator');
    } else if (templateId === 'fake-call-generator') {
      navigate('/tools/fake-call-generator');
    } else {
      navigate(`/templates/${templateId}`);
    }
  };

  // Template categories for filter
  const templateCategories = [
    { key: 'All', label: 'All', icon: 'mdi:view-grid' },
    { key: 'Content', label: 'Content', icon: 'mdi:file-document' },
    { key: 'Analytics', label: 'Engagement', icon: 'mdi:chart-line' },
    { key: 'Automation', label: 'Fun', icon: 'mdi:party-popper' }
  ];

  // Get templates for current category
  const currentTemplates = templateCategory === 'All'
    ? workflowTemplates
    : workflowTemplates.filter(t => t.type.toLowerCase() === templateCategory.toLowerCase());

  // Filter templates based on search query
  const filteredTemplates = searchQuery
    ? workflowTemplates.filter(t =>
        t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Main Content */}
      <div className="flex h-screen">
        {/* Sidebar */}
        <Sidebar activePage="projects" />

        {/* Main Area */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="px-10 py-5 border-b border-gray-200 flex items-center gap-4">
            <div className="flex-1 relative">
              <Icon icon="mdi:magnify" className="absolute left-4 top-1/2 -translate-y-1/2 text-xl text-gray-400" />
              <input
                type="text"
                placeholder={t('common.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full py-2.5 pl-11 pr-16 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm outline-none focus:border-gray-400"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                Shift + S
              </span>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 p-10 overflow-y-auto">
            {/* Quick Start - Templates Section (only on /home) */}
            {!searchQuery && isHomePage && (
              <>
                <h2 className="mb-4 text-2xl font-bold text-gray-900">
                  {t('projectsPage.quickStart')}
                </h2>

                {/* Category Tabs */}
                <div className="flex items-center gap-2 mb-6 flex-wrap">
                  {templateCategories.map((category) => (
                    <PillButton
                      key={category.key}
                      onClick={() => setTemplateCategory(category.key)}
                      selected={templateCategory === category.key}
                      icon={category.icon}
                    >
                      {category.label}
                    </PillButton>
                  ))}
                </div>

                {/* Templates Section - All templates in one grid */}
                <div className="mb-16">
                  {/* Template Cards Grid */}
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-6">
                    {currentTemplates.map(template => (
                      <div
                        key={template.id}
                        onClick={() => handleTemplateClick(template.id)}
                        className="bg-gray-50 border border-gray-200 rounded-xl p-6 cursor-pointer transition-all relative hover:border-gray-900 hover:-translate-y-0.5"
                      >
                        <Icon icon={template.icon} className="text-[32px] text-gray-900 mb-4" />
                        <h3 className="mb-2 text-lg font-semibold text-gray-900">
                          {template.title}
                        </h3>
                        <p className="mb-3 text-sm text-gray-500 leading-relaxed">
                          {template.description}
                        </p>
                        <div className="flex items-center gap-1.5 text-sm text-gray-400">
                          <Icon icon="mdi:lightning-bolt" className="text-base" />
                          {template.type}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Your Projects */}
            {!searchQuery && (
              <>
                <h2 className="mb-6 text-2xl font-bold text-gray-900">
                  {t('projectsPage.yourProjects')}
                </h2>
                <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-6">
                  {/* 草稿卡片 - Draft Progress (從 Supabase 讀取) - Mind Palace 風格 */}
                  {draftProgress.map(draft => (
                    <MindPalaceProjectCard
                      key={draft.id}
                      project={{
                        ...draft,
                        // 確保有 name/topic 給卡片顯示
                        name: draft.topic || draft.keywords?.[0] || draft.name || '內容生成草稿',
                        topic: draft.topic || draft.keywords?.[0] || draft.name || '內容生成草稿',
                      }}
                      onClick={() => handleContinueDraft(draft)}
                      menuSlot={
                        <CardMenu
                          options={[
                            {
                              label: '繼續編輯',
                              icon: 'mdi:pencil',
                              onClick: () => handleContinueDraft(draft)
                            },
                            { divider: true },
                            {
                              label: '刪除草稿',
                              icon: 'mdi:delete',
                              danger: true,
                              onClick: async () => {
                                const confirmed = await confirm({
                                  title: '刪除草稿',
                                  message: '確定要刪除此草稿嗎？',
                                  confirmText: '刪除',
                                  cancelText: '取消',
                                  variant: 'destructive'
                                });
                                if (confirmed) {
                                  handleDeleteDraft(draft.id);
                                }
                              }
                            }
                          ]}
                        />
                      }
                    />
                  ))}

                  {/* 自動經營 Projects */}
                  {autoManagedProjects.map(project => (
                    <div
                      key={`auto-${project.id}`}
                      onClick={() => {
                        if (project.isProject) {
                          // 新格式：導航到 project 詳情頁
                          navigate(`/project/${project.id}`);
                        } else {
                          // 舊格式：導航到 step4，帶上 accountId
                          navigate(`/templates/content-generator/step4?accountId=${project.settings?.accountId}`);
                        }
                      }}
                      className="bg-gradient-to-br from-green-50 to-green-100 border border-green-300 rounded-xl p-5 cursor-pointer transition-all relative overflow-hidden hover:-translate-y-1 hover:shadow-lg hover:shadow-emerald-500/20"
                    >
                      {/* 運行中標籤 */}
                      <div className="absolute top-3 right-3 bg-emerald-500 text-white px-2.5 py-1 rounded-full text-[11px] font-semibold flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                        運行中
                      </div>

                      {/* Preview Area */}
                      <div className="w-full h-[120px] bg-white/60 rounded-lg mb-4 flex flex-col items-center justify-center gap-2">
                        <Icon icon="mdi:robot-outline" className="text-4xl text-emerald-500" />
                        <div className="text-xs text-green-800 font-medium">
                          {project.settings?.strategy === 'topic' ? '📚 主題式' :
                           project.settings?.strategy === 'news' ? '📰 新聞追蹤' : '🎲 隨機故事'}
                        </div>
                      </div>

                      {/* Project Info */}
                      <h3 className="mb-2 text-base font-semibold text-green-800">
                        {project.name || project.settings?.accountName || 'Auto Post'}
                      </h3>
                      <p className="mb-3 text-[13px] text-green-700">
                        {project.settings?.postingTimes?.length || 0} 個發文時段 · {project.settings?.keywords?.length || 0} 個關鍵字
                      </p>

                      {/* Badge */}
                      <div className="inline-block px-2.5 py-1 bg-green-800/10 rounded-md text-xs text-green-800 font-medium">
                        自動經營
                      </div>
                    </div>
                  ))}

                  {/* Existing Projects */}
                  {isLoadingProjects ? (
                    <div className="col-span-full">
                      <ContentSpinner text="Loading projects..." />
                    </div>
                  ) : projects.length > 0 ? (
                    projects
                      .filter(project => true) // Show all projects
                      .map(project => (
                      <ProjectCard
                        key={project.id}
                        project={project}
                        theme={theme}
                        toast={toast}
                        confirm={confirm}
                        onClick={() => {
                          // Navigate to project detail page
                          navigate(`/project/${project.id}`);
                        }}
                        onDelete={(id) => {
                          setProjects(projects.filter(p => p.id !== id));
                        }}
                        onUpdate={(updatedProject) => {
                          setProjects(projects.map(p =>
                            p.id === updatedProject.id ? updatedProject : p
                          ));
                        }}
                      />
                    ))
                  ) : (
                    draftProgress.length === 0 && autoManagedProjects.length === 0 && (
                      <div className="col-span-full p-10 text-center text-gray-400">
                        <Icon icon="mdi:folder-open-outline" className="text-5xl" />
                        <p className="mt-4">No projects yet. Use templates above to create content!</p>
                      </div>
                    )
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}

function ProjectCard({ project, theme, toast, confirm, onClick, onDelete, onUpdate }) {
  const [isRenaming, setIsRenaming] = useState(false);
  const [newName, setNewName] = useState(project.name);

  const handleRename = async () => {
    try {
      const response = await apiPut(`/projects/${project.id}`, {
        name: newName
      });

      if (response.success && onUpdate) {
        onUpdate(response.project);
        toast.success('Project renamed successfully');
      }
      setIsRenaming(false);
    } catch (error) {
      console.error('Failed to rename project:', error);
      toast.error('Failed to rename project. Please try again.');
    }
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: 'Move to Trash',
      message: `Move "${project.name}" to trash? It will be permanently deleted after 7 days.`,
      confirmText: 'Move to Trash',
      cancelText: 'Cancel',
      variant: 'destructive'
    });

    if (!confirmed) return;

    try {
      const response = await fetch(`http://localhost:3001/api/projects/${project.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok && onDelete) {
        onDelete(project.id);
        toast.success('Project moved to trash');
      } else {
        throw new Error('Failed to move project to trash');
      }
    } catch (error) {
      console.error('Failed to move project to trash:', error);
      toast.error('Failed to move project to trash. Please try again.');
    }
  };

  const handleDuplicate = async () => {
    console.log('Duplicate project:', project.id);
  };

  // Menu 組件
  const menuSlot = (
    <CardMenu
      options={[
        { label: 'Open', icon: 'mdi:folder-open-outline', onClick },
        { label: 'Rename', icon: 'mdi:pencil-outline', onClick: () => setIsRenaming(true) },
        { label: 'Add to favorites', icon: 'mdi:star-outline', onClick: () => console.log('Add to favorites') },
        { label: 'Share', icon: 'mdi:share-variant-outline', onClick: () => console.log('Share project') },
        { label: 'Duplicate', icon: 'mdi:content-duplicate', onClick: handleDuplicate },
        { label: 'Move project', icon: 'mdi:folder-move-outline', onClick: () => console.log('Move project') },
        { divider: true },
        { label: 'Delete', icon: 'mdi:trash-can-outline', onClick: handleDelete, danger: true }
      ]}
    />
  );

  return (
    <>
      {/* Mind Palace Style Project Card */}
      <MindPalaceProjectCard
        project={project}
        onClick={onClick}
        menuSlot={menuSlot}
      />

      {/* Rename Modal */}
      {isRenaming && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]"
        >
          <div className="bg-white rounded-xl p-6 w-[400px] max-w-[90%]">
            <h3 className="mb-4 text-lg font-semibold text-gray-900">
              Rename Project
            </h3>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleRename();
                }
              }}
              autoFocus
              className="w-full p-3 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-900 mb-4 box-border focus:border-gray-400 outline-none"
            />
            <div className="flex gap-2 justify-end">
              <Button
                variant="secondary"
                size="md"
                onClick={() => {
                  setIsRenaming(false);
                  setNewName(project.name);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleRename}
                disabled={!newName.trim()}
              >
                Rename
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ProjectsPage;
